/**
 * #5068 — the 3D parts kit. Each item builds its mesh as plain numbers (positions and triangle indices), with no
 * three.js dependency, so the server (hit boxes, anchors, triangle budget) and every browser build identical
 * geometry. Parts are placed on the same 960 × 540 sheet as the 2D kit, plus depth (`z`), so the same declared joints
 * (crank, cam, gears) drive both.
 *
 * Mesh space: a unit box of width 1 centred on the origin, x right, y UP, z toward the viewer. Anchors are given the
 * 2D kit's way (sheet orientation, y down), so motion code reads both kits alike. Absolute items (springs, rods, pipes)
 * take sheet points and build their mesh in sheet units. Adding an item is ordinary repo work; programs cannot.
 */
import { z } from 'zod';
import { DrawingPointSchema, type DrawingColorRole, type DrawingPart } from './DrawingSchema';
import type { Point } from './sketchKit';

export interface Mesh3d { positions: number[]; indices: number[]; role?: DrawingColorRole }
export interface KitModel { meshes: Mesh3d[]; anchors?: Record<string, Point> }
interface Kit3dItem<P extends z.ZodTypeAny> { summary: string; absolute?: boolean; params: P; build: (params: z.infer<P>) => KitModel }
/** The most triangles a whole drawing may hold after its parts are built (spec: 20,000). */
export const DRAWING_MAX_TRIANGLES = 20000;

// ── mesh builders (pure numbers) ───────────────────────────────────────────────────────────────────────────────────
type V3 = [number, number, number];
const TAU = Math.PI * 2;
function mesh(role?: DrawingColorRole): Mesh3d & { v: (p: V3) => number; tri: (a: number, b: number, c: number) => void } {
  const out = { positions: [] as number[], indices: [] as number[], ...(role ? { role } : {}),
    v: (p: V3) => { out.positions.push(p[0], p[1], p[2]); return out.positions.length / 3 - 1; },
    tri: (a: number, b: number, c: number) => { out.indices.push(a, b, c); } };
  return out;
}
const finish = (m: ReturnType<typeof mesh>): Mesh3d => ({ positions: m.positions, indices: m.indices, ...(m.role ? { role: m.role } : {}) });

export function boxMesh(w: number, h: number, d: number, c: V3 = [0, 0, 0], role?: DrawingColorRole): Mesh3d {
  const m = mesh(role), [cx, cy, cz] = c, x = w / 2, y = h / 2, zz = d / 2;
  // Each face gets its own corners, so flat shading and outlines see sharp edges.
  const faces: V3[][] = [
    [[-x, -y, zz], [x, -y, zz], [x, y, zz], [-x, y, zz]], [[x, -y, -zz], [-x, -y, -zz], [-x, y, -zz], [x, y, -zz]],
    [[x, -y, zz], [x, -y, -zz], [x, y, -zz], [x, y, zz]], [[-x, -y, -zz], [-x, -y, zz], [-x, y, zz], [-x, y, -zz]],
    [[-x, y, zz], [x, y, zz], [x, y, -zz], [-x, y, -zz]], [[-x, -y, -zz], [x, -y, -zz], [x, -y, zz], [-x, -y, zz]],
  ];
  for (const face of faces) {
    const [a, b, cc, dd] = face.map(([px, py, pz]) => m.v([px + cx, py + cy, pz + cz]));
    m.tri(a, b, cc); m.tri(a, cc, dd);
  }
  return finish(m);
}

/**
 * A surface of revolution: `profile` is [radius, height] pairs, turned about the y axis (or the x axis), through
 * `sweep` of a full turn from `start` (for a cut-away).
 */
export function latheMesh(profile: Array<[number, number]>, options: { segments?: number; axis?: 'y' | 'x'; sweep?: number; start?: number; center?: V3; role?: DrawingColorRole } = {}): Mesh3d {
  const m = mesh(options.role), segments = Math.max(3, Math.min(64, options.segments ?? 28)), sweep = options.sweep ?? 1, start = options.start ?? 0;
  const [cx, cy, cz] = options.center ?? [0, 0, 0], closedRing = sweep >= 1;
  const columns = closedRing ? segments : segments + 1;
  const rings = profile.map(([radius, height]) => Array.from({ length: columns }, (_, i) => {
    const a = (start + sweep * i / segments) * TAU, u = Math.cos(a) * radius, w = Math.sin(a) * radius;
    return m.v(options.axis === 'x' ? [height + cx, u + cy, w + cz] : [u + cx, height + cy, w + cz]);
  }));
  for (let r = 0; r < rings.length - 1; r++) for (let i = 0; i < segments; i++) {
    const a = rings[r][i], b = rings[r][(i + 1) % columns], c = rings[r + 1][(i + 1) % columns], d = rings[r + 1][i];
    m.tri(a, b, c); m.tri(a, c, d);
  }
  return finish(m);
}

/** A flat outline (star-shaped about its centroid, as gears, cams and airfoils are) given `depth` along z. */
export function prismMesh(outline: Point[], depth: number, options: { center?: V3; role?: DrawingColorRole } = {}): Mesh3d {
  const m = mesh(options.role), [cx, cy, cz] = options.center ?? [0, 0, 0], d = depth / 2, n = outline.length;
  const gx = outline.reduce((s, p) => s + p[0], 0) / n, gy = outline.reduce((s, p) => s + p[1], 0) / n;
  for (const side of [d, -d]) {
    const centre = m.v([gx + cx, gy + cy, side + cz]), ring = outline.map(([x, y]) => m.v([x + cx, y + cy, side + cz]));
    for (let i = 0; i < n; i++) side > 0 ? m.tri(centre, ring[i], ring[(i + 1) % n]) : m.tri(centre, ring[(i + 1) % n], ring[i]);
  }
  for (let i = 0; i < n; i++) {
    const [ax, ay] = outline[i], [bx, by] = outline[(i + 1) % n];
    const a = m.v([ax + cx, ay + cy, d + cz]), b = m.v([bx + cx, by + cy, d + cz]), c = m.v([bx + cx, by + cy, -d + cz]), e = m.v([ax + cx, ay + cy, -d + cz]);
    m.tri(a, e, c); m.tri(a, c, b);
  }
  return finish(m);
}

/**
 * A round tube along a 3D path (springs, rods, pipes). The ring frame is carried from ring to ring (parallel
 * transport), so a helix does not twist and pinch where its tangent turns toward the viewer.
 */
export function tubeMesh(path: V3[], radius: number, options: { segments?: number; role?: DrawingColorRole } = {}): Mesh3d {
  const m = mesh(options.role), segments = Math.max(3, Math.min(24, options.segments ?? 8)), rings: number[][] = [];
  let normal: V3 | null = null;
  for (let i = 0; i < path.length; i++) {
    const prev = path[Math.max(0, i - 1)], next = path[Math.min(path.length - 1, i + 1)];
    let tx = next[0] - prev[0], ty = next[1] - prev[1], tz = next[2] - prev[2];
    const tl = Math.hypot(tx, ty, tz) || 1; tx /= tl; ty /= tl; tz /= tl;
    let nx = 0, ny = 0, nz = 0;
    if (normal) {
      // The previous ring's normal, with its tangent part removed.
      const dot = normal[0] * tx + normal[1] * ty + normal[2] * tz;
      nx = normal[0] - tx * dot; ny = normal[1] - ty * dot; nz = normal[2] - tz * dot;
    }
    if (Math.hypot(nx, ny, nz) < 1e-6) {
      // First ring (or the frame collapsed): any vector not parallel to the tangent.
      const up: V3 = Math.abs(tz) < .9 ? [0, 0, 1] : [0, 1, 0];
      nx = ty * up[2] - tz * up[1]; ny = tz * up[0] - tx * up[2]; nz = tx * up[1] - ty * up[0];
    }
    const nl = Math.hypot(nx, ny, nz) || 1; nx /= nl; ny /= nl; nz /= nl;
    normal = [nx, ny, nz];
    const bx = ty * nz - tz * ny, by = tz * nx - tx * nz, bz = tx * ny - ty * nx;
    rings.push(Array.from({ length: segments }, (_, k) => {
      const a = k / segments * TAU, c = Math.cos(a) * radius, s = Math.sin(a) * radius;
      return m.v([path[i][0] + nx * c + bx * s, path[i][1] + ny * c + by * s, path[i][2] + nz * c + bz * s]);
    }));
  }
  for (let i = 0; i < rings.length - 1; i++) for (let k = 0; k < segments; k++) {
    const a = rings[i][k], b = rings[i][(k + 1) % segments], c = rings[i + 1][(k + 1) % segments], d = rings[i + 1][k];
    m.tri(a, b, c); m.tri(a, c, d);
  }
  return finish(m);
}

const circle = (r: number, n: number, cx = 0, cy = 0): Point[] => Array.from({ length: n }, (_, i): Point => [cx + Math.cos(i / n * TAU) * r, cy + Math.sin(i / n * TAU) * r]);
// Each parameter describes its range for the program prompt's catalogue (drawingPrompt.ts).
const n = (min: number, max: number, fallback: number) => z.number().finite().catch(fallback).transform(value => Math.min(max, Math.max(min, value))).default(fallback)
  .describe(`${min}…${max}, default ${fallback}`);
const sheetPoint = (fallback: Point) => DrawingPointSchema.catch(fallback).default(fallback).describe('[x, y] on the sheet');
const points = DrawingPointSchema.array();
/** Sheet points (y down) to mesh space for absolute items: x right, y up, at depth `z`. */
const sheet3 = ([x, y]: Point, zz = 0): V3 => [x, -y, zz];
const item = <P extends z.ZodTypeAny>(definition: Kit3dItem<P>) => definition;

export const PARTS_KIT_3D = {
  shaft3d: item({ summary: 'a round shaft or axle along its width (thickness = diameter ÷ length)', params: z.object({ thickness: n(.02, .5, .08) }),
    build: p => ({ meshes: [latheMesh([[0, -.5], [p.thickness / 2, -.5], [p.thickness / 2, .5], [0, .5]], { axis: 'x', segments: 18 })] }) }),
  gear3d: item({ summary: 'a spur gear with teeth, seen face-on (thickness = depth ÷ width)', params: z.object({ teeth: n(6, 40, 14), thickness: n(.05, .6, .18) }),
    build: p => { const teeth = Math.round(p.teeth), outline: Point[] = [];
      for (let i = 0; i < teeth; i++) { const a = i / teeth * TAU, w = TAU / teeth;
        for (const [da, r] of [[0, .4], [.15, .5], [.45, .5], [.6, .4]] as const) outline.push([Math.cos(a + w * da) * r, Math.sin(a + w * da) * r]); }
      return { meshes: [prismMesh(outline, p.thickness)], anchors: { center: [0, 0] } }; } }),
  cam3d: item({ summary: 'a cam: a disc with one lobe (lobe 0.1–0.5) pushing a follower once per turn', params: z.object({ lobe: n(.1, .5, .3), thickness: n(.05, .6, .16) }),
    build: p => ({ meshes: [prismMesh(Array.from({ length: 40 }, (_, i): Point => { const a = i / 40 * TAU, bump = Math.max(0, Math.cos(a - Math.PI / 2)) ** 3 * p.lobe;
      return [Math.cos(a) * (.3 + bump), Math.sin(a) * (.3 + bump)]; }), p.thickness)], anchors: { center: [0, 0] } }) }),
  crank3d: item({ summary: 'a crank: a disc with a pin at `throw` (anchor "pin")', params: z.object({ throw: n(.1, .45, .32) }),
    build: p => ({ meshes: [prismMesh(circle(.5, 32), .14), tubeMesh([[0, p.throw, -.22], [0, p.throw, .22]], .06, { segments: 12 })],
      anchors: { center: [0, 0], pin: [0, -p.throw] } }) }),
  piston3d: item({ summary: 'a piston with ring grooves and a wrist pin (anchor "pin")', params: z.object({ height: n(.4, 1.5, .7) }),
    build: p => { const h = p.height / 2, g = .02;
      return { meshes: [latheMesh([[0, h], [.5, h], [.5, h - .1], [.47, h - .1], [.47, h - .1 - g], [.5, h - .1 - g], [.5, h - .2], [.47, h - .2], [.47, h - .2 - g],
        [.5, h - .2 - g], [.5, -h], [0, -h]]), tubeMesh([[0, -p.height * .15, -.55], [0, -p.height * .15, .55]], .08, { segments: 12 })],
        anchors: { pin: [0, p.height * .15], top: [0, -h] } }; } }),
  cylinder3d: item({ summary: 'an engine cylinder cut open (back half and head), depth = height ÷ width', params: z.object({ depth: n(.5, 4, 1.6), wall: n(.03, .2, .08) }),
    build: p => { const d = p.depth / 2, w = p.wall;
      return { meshes: [latheMesh([[.5, d], [.5 + w, d], [.5 + w, -d], [.5, -d], [.5, d]], { sweep: .5, start: .5, segments: 20 }),
        latheMesh([[0, d], [.5 + w, d], [.5 + w, d - w], [0, d - w]], { sweep: .5, start: .5, segments: 20 })],
        anchors: { head: [0, -d + w], bottom: [0, d] } }; } }),
  valve3d: item({ summary: 'a poppet valve: a mushroom head on a stem (stem = length ÷ width)', params: z.object({ stem: n(.5, 4, 1.4) }),
    build: p => ({ meshes: [latheMesh([[0, 0], [.5, 0], [.5, .05], [.1, .18], [.05, .3], [.05, p.stem], [0, p.stem]], { segments: 20 })],
      anchors: { tip: [0, -p.stem], head: [0, 0] } }) }),
  spring3d: item({ summary: 'a coil spring between two sheet points', absolute: true,
    params: z.object({ from: sheetPoint([0, 0]), to: sheetPoint([0, 60]), coils: n(3, 16, 7), width: n(4, 80, 18) }),
    build: p => { const [ax, ay] = p.from as Point, [bx, by] = p.to as Point, len = Math.hypot(bx - ax, by - ay) || 1, ux = (bx - ax) / len, uy = (by - ay) / len;
      const steps = Math.round(p.coils) * 12, r = p.width / 2;
      const path = Array.from({ length: steps + 1 }, (_, i): V3 => { const t = i / steps, a = t * Math.round(p.coils) * TAU;
        const sx = ax + ux * len * t - uy * Math.cos(a) * r, sy = ay + uy * len * t + ux * Math.cos(a) * r;
        return [sx, -sy, Math.sin(a) * r]; });
      return { meshes: [tubeMesh(path, Math.max(1, p.width * .06), { segments: 6 })] }; } }),
  link3d: item({ summary: 'a connecting rod between two sheet points, with an eye at each end', absolute: true,
    params: z.object({ from: sheetPoint([0, 0]), to: sheetPoint([0, 100]), width: n(4, 60, 14) }),
    build: p => ({ meshes: [tubeMesh([sheet3(p.from as Point), sheet3(p.to as Point)], p.width * .35, { segments: 16 }),
      tubeMesh([sheet3(p.from as Point, -p.width * .6), sheet3(p.from as Point, p.width * .6)], p.width * .6, { segments: 16 }),
      tubeMesh([sheet3(p.to as Point, -p.width * .5), sheet3(p.to as Point, p.width * .5)], p.width * .5, { segments: 16 })] }) }),
  pipe3d: item({ summary: 'a pipe through sheet points', absolute: true, params: z.object({ points: points.min(2).max(32).catch([[0, 0], [100, 0]]), bore: n(4, 60, 16) }),
    build: p => ({ meshes: [tubeMesh((p.points as Point[]).map(point => sheet3(point)), p.bore / 2, { segments: 16 })] }) }),
  nozzle3d: item({ summary: 'a nozzle tapering to the right (exit = outlet ÷ inlet)', params: z.object({ exit: n(.2, 1, .55) }),
    build: p => ({ meshes: [latheMesh([[.26, -.5], [.26 * p.exit, .5]], { axis: 'x', segments: 24 })] }) }),
  fan3d: item({ summary: 'a fan or rotor seen face-on: a hub and twisted blades', params: z.object({ blades: n(2, 12, 6) }),
    build: p => ({ meshes: [tubeMesh([[0, 0, -.08], [0, 0, .08]], .1, { segments: 16 }),
      ...Array.from({ length: Math.round(p.blades) }, (_, i) => spinMesh(boxMesh(.38, .1, .02, [.29, 0, 0]), i / Math.round(p.blades) * TAU, .5))], anchors: { center: [0, 0] } }) }),
  wing3d: item({ summary: 'a wing: an airfoil section (leading edge left) swept back along its span (span = length ÷ chord)', params: z.object({ span: n(.5, 6, 2.5) }),
    build: p => { const top = Array.from({ length: 16 }, (_, i): Point => { const t = i / 15; return [t - .5, .12 * Math.sin(Math.PI * t) * (1 - t * .5)]; });
      const bottom = Array.from({ length: 14 }, (_, i): Point => { const t = 1 - (i + 1) / 15; return [t - .5, -.03 * Math.sin(Math.PI * t)]; });
      return { meshes: [prismMesh([...top, ...bottom], p.span)] }; } }),
  fin3d: item({ summary: 'a tail fin or blade: a thin swept triangle', params: z.object({ height: n(.3, 3, 1) }),
    build: p => ({ meshes: [prismMesh([[-.5, 0], [.5, 0], [.4, p.height], [.2, p.height]], .05)] }) }),
  body3d: item({ summary: 'a streamlined body (fuselage, rocket) along its length, nose left (girth = diameter ÷ length)', params: z.object({ girth: n(.05, .6, .18), tail: n(0, 1, .4) }),
    build: p => { const r = p.girth / 2;
      return { meshes: [latheMesh([[0, -.5], [r * .55, -.42], [r * .9, -.3], [r, -.15], [r, .3], [r * (.4 + .6 * (1 - p.tail)), .5], [0, .5]], { axis: 'x', segments: 24 })] }; } }),
  block3d: item({ summary: 'a solid block or housing (aspect = height ÷ width, deep = depth ÷ width)', params: z.object({ aspect: n(.1, 4, .6), deep: n(.1, 4, .6) }),
    build: p => ({ meshes: [boxMesh(1, p.aspect, p.deep)] }) }),
  bearing3d: item({ summary: 'a ring bearing seen face-on', params: z.object({}),
    build: () => ({ meshes: [rotateMeshToZ(latheMesh([[.3, -.1], [.5, -.1], [.5, .1], [.3, .1], [.3, -.1]], { segments: 28 }), [0, 0, 0])], anchors: { center: [0, 0] } }) }),
  ball3d: item({ summary: 'a ball or sphere', params: z.object({}),
    build: () => ({ meshes: [latheMesh(Array.from({ length: 11 }, (_, i): [number, number] => [Math.sin(i / 10 * Math.PI) * .5, -Math.cos(i / 10 * Math.PI) * .5]), { segments: 20 })] }) }),
  ground3d: item({ summary: 'the ground: a wide flat slab', params: z.object({ deep: n(.1, 4, 1) }),
    build: p => ({ meshes: [boxMesh(1, .02, p.deep, [0, 0, 0], 'life')] }) }),
  person3d: item({ summary: 'a person, for scale', params: z.object({}),
    build: () => ({ meshes: [latheMesh([[0, -1], [.18, -1], [.2, .3], [0, .3]], { segments: 14 }),
      latheMesh(Array.from({ length: 9 }, (_, i): [number, number] => [Math.sin(i / 8 * Math.PI) * .22, .55 - Math.cos(i / 8 * Math.PI) * .22]), { segments: 14 })] }) }),
} as const;

/** Turns a mesh built about the y axis to face along z (for discs seen face-on), about `centre`. */
function rotateMeshToZ(meshIn: Mesh3d, centre: V3): Mesh3d {
  const positions = [...meshIn.positions];
  for (let i = 0; i < positions.length; i += 3) {
    const y = positions[i + 1] - centre[1], zz = positions[i + 2] - centre[2];
    positions[i + 1] = centre[1] - zz; positions[i + 2] = centre[2] + y;
  }
  return { ...meshIn, positions };
}
/** Spins a mesh about the z axis by `angle`, with a blade twist about its own long axis. */
function spinMesh(meshIn: Mesh3d, angle: number, twist: number): Mesh3d {
  const positions = [...meshIn.positions], c = Math.cos(angle), s = Math.sin(angle), tc = Math.cos(twist), ts = Math.sin(twist);
  for (let i = 0; i < positions.length; i += 3) {
    const x = positions[i], y0 = positions[i + 1], z0 = positions[i + 2];
    const y = y0 * tc - z0 * ts, zz = y0 * ts + z0 * tc;
    positions[i] = x * c - y * s; positions[i + 1] = x * s + y * c; positions[i + 2] = zz;
  }
  return { ...meshIn, positions };
}

export type PartsKit3dName = keyof typeof PARTS_KIT_3D;
export const PARTS_KIT_3D_NAMES = Object.keys(PARTS_KIT_3D) as PartsKit3dName[];
export function isPartsKit3dName(name: string): name is PartsKit3dName { return Object.prototype.hasOwnProperty.call(PARTS_KIT_3D, name); }
export function isAbsolutePartsKit3d(name: string): boolean { return isPartsKit3dName(name) && !!(PARTS_KIT_3D[name] as { absolute?: boolean }).absolute; }

const parsed3d = new WeakMap<object, Record<string, unknown> | null>();
export function partsKit3dParams(part: Pick<DrawingPart, 'kit' | 'params'>): Record<string, unknown> | null {
  if (!isPartsKit3dName(part.kit)) return null;
  if (parsed3d.has(part)) return parsed3d.get(part)!;
  const result = (PARTS_KIT_3D[part.kit].params as z.ZodTypeAny).safeParse(part.params ?? {});
  const value = result.success ? result.data : null;
  parsed3d.set(part, value);
  return value;
}

/** A part's mesh in its own space (absolute items: sheet units with y up), with `ends` overriding a spring's or rod's. */
export function buildPart3d(part: DrawingPart, ends?: { from?: Point; to?: Point }): KitModel | null {
  const params = partsKit3dParams(part);
  if (!params || !isPartsKit3dName(part.kit)) return null;
  const kit = PARTS_KIT_3D[part.kit] as unknown as Kit3dItem<z.ZodTypeAny>;
  return kit.build(kit.absolute && ends ? { ...params, ...ends } : params);
}
export function meshTriangles(model: KitModel | null): number {
  return model ? model.meshes.reduce((sum, item) => sum + item.indices.length / 3, 0) : 0;
}

/**
 * A 3D part's footprint on the sheet (for labels, hit boxes and anchors), after placement and an optional motion:
 * the same rules as `placeSketchPart`, read from the mesh's x and y.
 */
export function placePart3d(part: DrawingPart, motion: { offset?: Point; turn?: number; ends?: { from?: Point; to?: Point } } = {}):
  { anchors: Record<string, Point>; box: { x: number; y: number; width: number; height: number } } | null {
  const model = buildPart3d(part, motion.ends);
  if (!model || !isPartsKit3dName(part.kit)) return null;
  const absolute = isAbsolutePartsKit3d(part.kit), [ox, oy] = motion.offset ?? [0, 0];
  // The renderer's order (Euler XYZ = Rx·Ry·Rz): spin in the sheet's plane first, then yaw, then tilt; then the view
  // looks along z, so only x and y land on the sheet. Mesh y is up; the sheet's is down.
  const spin = -(part.rotate * Math.PI / 180 + (motion.turn ?? 0)), yaw = part.yaw * Math.PI / 180, tilt = part.tilt * Math.PI / 180;
  const [cs, ss, cy, sy, ct, st] = [Math.cos(spin), Math.sin(spin), Math.cos(yaw), Math.sin(yaw), Math.cos(tilt), Math.sin(tilt)];
  const place = (x: number, yUp: number, zz: number): Point => {
    if (absolute) return [x + ox, -yUp + oy];
    const x1 = x * cs - yUp * ss, y1 = x * ss + yUp * cs;
    const x2 = x1 * cy + zz * sy, z2 = -x1 * sy + zz * cy;
    const y3 = y1 * ct - z2 * st;
    return [part.x + ox + x2 * part.size, part.y + oy - y3 * part.size];
  };
  const xs: number[] = [], ys: number[] = [];
  for (const item of model.meshes) for (let i = 0; i < item.positions.length; i += 3) {
    const [x, y] = place(item.positions[i], item.positions[i + 1], item.positions[i + 2]);
    xs.push(x); ys.push(y);
  }
  // Anchors are given in sheet orientation (y down) on the part's plane.
  const anchors = Object.fromEntries(Object.entries(model.anchors ?? {}).map(([name, [ax, ay]]) => [name, place(ax, -ay, 0)]));
  return { anchors, box: xs.length ? { x: Math.min(...xs), y: Math.min(...ys), width: Math.max(...xs) - Math.min(...xs), height: Math.max(...ys) - Math.min(...ys) }
    : { x: part.x, y: part.y, width: 0, height: 0 } };
}

export function partsKit3dCatalogue(): string {
  return PARTS_KIT_3D_NAMES.map(name => `${name}${(PARTS_KIT_3D[name] as { absolute?: boolean }).absolute ? ' (sheet points)' : ''}: ${PARTS_KIT_3D[name].summary}`).join('\n');
}
